import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Button, Tag, Dropdown, Badge } from "antd";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
import {
  PlusOutlined,
  MoreOutlined,
  EyeOutlined,
  ArrowRightOutlined,
  FilterOutlined,
  InboxOutlined, // Add this icon for Archive
} from "@ant-design/icons";
import MetricsGrid from "@/components/UI/Layout/MetricGrid";
import { getStatusColor, formatCurrency, formatDate } from "@/utils/helper";
import { useGetCateringEventQuery } from "@/services/api/cateringevent/cateringEventApi";

const OrderManager = ({
  tableData = [],
  metricData,
  tabs,
  showBookedEvent,
  buttonText,
  tableTitle,
  onCreate,
  isLoading,
  openDetailsModal,
  openCateringPackageOrderDetailsModal,
  openBookedEventModal,
  onBulkFulfillment,
  onBulkArchive, // Add this prop
  archiving = false, // Add this prop
  paginationData,
  onPageChange,
  onPageSizeChange,
  search = "",
  handleSearch,
  searchMode = "client",
  onFilterClick,
  hasActiveFilters = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { data: bookedEvent, isLoading: bookedEventLoading } =
    useGetCateringEventQuery(undefined, { skip: !showBookedEvent });
  const bookedEventsData = useMemo(() => {
    if (!bookedEvent?.data) return [];

    return bookedEvent?.data.map((event) => ({
      key: event.id.toString(),
      orderId: `EVT-${event.id}`,
      customer: event.customer_name,
      orderDate: formatDate(event.created_at),
      branch: event.pickup_address,
      orderDueDate: formatDate(event.event_date),
      amount: formatCurrency(event.total_budget),
      items: event.expected_guests,
      status: "Booked Event",
      orderType: "event",
      originalData: event,
    }));
  }, [bookedEvent]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSelectedRowKeys([]);
    if (onPageChange) {
      onPageChange(1);
    }
  };

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [paginationData?.current_page]);

  // Filtered tableData based on status
  const incomingOrders = tableData.filter(
    (order) => order.status === "Incoming"
  );
  const confirmedOrders = tableData.filter(
    (order) => order.status === "Confirmed"
  );
  const processingOrders = tableData.filter(
    (order) => order.status === "Processing"
  );
  const availableOrders = tableData.filter(
    (order) => order.status === "Available"
  );
  const pickupOrders = tableData.filter((order) => order.status === "Pick up");
  const deliveryOrders = tableData.filter(
    (order) => order.status === "Delivered"
  );

  const allOrdersData = showBookedEvent
    ? [...tableData, ...bookedEventsData].sort(
        (a, b) =>
          new Date(b.originalData?.created_at || 0) -
          new Date(a.originalData?.created_at || 0),
      )
    : tableData;

  const tableDataMap = {
    0: allOrdersData,
    1: incomingOrders,
    2: confirmedOrders,
    3: processingOrders,
    4: availableOrders,
    5: pickupOrders,
    6: deliveryOrders,
    ...(showBookedEvent && { 7: bookedEventsData }),
  };

  // Bulk fulfillment button configuration
  const bulkFulfillmentConfig = {
    1: { text: "Move to Confirmed", stage: 1 },
    2: { text: "Move to Processing", stage: 2 },
    3: { text: "Move to Available", stage: 3 },
    4: { text: "Move to Pickup", stage: 4 },
    5: { text: "Move to Delivery", stage: 5 },
  };

  const showBulkFulfillment =
    bulkFulfillmentConfig[activeTab] && selectedRowKeys.length > 0;

  // Show Bulk Archive button only on Delivery tab (index 6) when orders are selected
  const showBulkArchive = activeTab === 6 && selectedRowKeys.length > 0;

  // Handle opening the correct modal based on orderType
  const handleViewDetails = (record) => {
    if (record.orderType === 2) {
      openCateringPackageOrderDetailsModal(record);
    } else if (bookedEventsData && record.orderType === "event") {
      openBookedEventModal(record);
    } else {
      openDetailsModal(record);
    }
  };

  const getActionMenuItems = (record) => [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "View Details",
      onClick: () => handleViewDetails(record),
    },
  ];

  const ordersColumns = [
    {
      title: "Order Id",
      dataIndex: "orderId",
      key: "orderId",
      width: 120,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 160,
    },
    {
      title: "Date and Time of Order",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 180,
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branch",
      width: 120,
    },
    {
      title: "Order due by",
      dataIndex: "orderDueDate",
      key: "orderDueDate",
      width: 180,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
    },
    {
      title: "Items",
      dataIndex: "items",
      key: "items",
      width: 80,
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const { color, backgroundColor } = getStatusColor(status);
        return (
          <Tag
            style={{
              color,
              backgroundColor,
              border: `1px solid ${color}20`,
              borderRadius: "8px",
              padding: "2px 8px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getActionMenuItems(record),
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            className="text-gray-400 hover:text-gray-600"
          />
        </Dropdown>
      ),
    },
  ];

  const ordersTabs = [
    { label: "All", count: allOrdersData.length },
    { label: "Incoming", count: incomingOrders.length },
    { label: "Confirmed", count: confirmedOrders.length },
    { label: "Processing", count: processingOrders.length },
    { label: "Available", count: availableOrders.length },
    { label: "Pickup", count: pickupOrders.length },
    { label: "Delivery", count: deliveryOrders.length },
    ...(showBookedEvent
      ? [
          {
            label: "Booked Event",
            count: bookedEventsData.length,
          },
        ]
      : []),
  ];

  // Build action buttons array dynamically
  const actionButtons = [
    ...(showBulkFulfillment
      ? [
          {
            label: bulkFulfillmentConfig[activeTab].text,
            type: "default",
            icon: <ArrowRightOutlined />,
            className: "border-orange-500 text-orange-500 hover:bg-orange-50",
            onClick: () =>
              onBulkFulfillment?.(
                bulkFulfillmentConfig[activeTab].stage,
                selectedRowKeys
              ),
          },
        ]
      : []),
    ...(showBulkArchive && onBulkArchive
      ? [
          {
            label: "Archive Orders",
            type: "default",
            icon: <InboxOutlined />,
            className: "border-blue-500 text-blue-500 hover:bg-blue-50",
            loading: archiving,
            onClick: () => onBulkArchive(selectedRowKeys),
          },
        ]
      : []),
    ...(buttonText
      ? [
          {
            label: buttonText,
            type: "primary",
            icon: <PlusOutlined />,
            className:
              "bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:bg-gradient-to-l hover:from-[#F9A022] hover:to-[#D06D01] border-0",
            onClick: onCreate,
          },
        ]
      : []),
  ];

  return (
    <div className="px-6 bg-gray-50 min-h-full">
      <MetricsGrid data={metricData} />
      <Row>
        <Col span={24}>
          <CustomDataTable
            title={tableTitle}
            data={tableDataMap[activeTab] || tableData}
            columns={ordersColumns}
            tabs={tabs || ordersTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            showSearch={true}
            searchPlaceholder="Search orders..."
            searchMode={searchMode}
            searchValue={search}
            onServerSearch={handleSearch}
            actionButtons={actionButtons}
            showFilter={true}
            onFilterClick={onFilterClick}
            filterButton={
              onFilterClick ? (
                <Badge dot={hasActiveFilters} offset={[-4, 4]}>
                  <Button
                    icon={<FilterOutlined />}
                    onClick={onFilterClick}
                    className={`${
                      hasActiveFilters
                        ? "border-orange-500 text-orange-500 bg-orange-50"
                        : ""
                    }`}
                  >
                    Filter
                  </Button>
                </Badge>
              ) : null
            }
            showCSV={true}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            selectedRowKeys={selectedRowKeys}
            loading={isLoading || bookedEventLoading}
            paginationMode="server"
            currentPage={paginationData?.current_page || 1}
            totalItems={paginationData?.total || tableData.length}
            serverPageSize={paginationData?.per_page || 10}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={["10", "20", "50", "100"]}
          />
        </Col>
      </Row>
    </div>
  );
};

export default OrderManager;
